/**
 * Porte do `setShare` do app original — o comportamento do **input** de mix.
 *
 * Não é cálculo: o motor renormaliza a alocação na leitura de qualquer jeito. O que
 * está aqui decide o que é **gravado** quando o usuário digita uma porcentagem, e o
 * app tem uma regra específica: mexer num recurso redistribui o resto
 * proporcionalmente, de forma que a etapa continue com 100% da demanda. Sem isso,
 * digitar 60% num recurso deixaria a linha somando 1,6 e o resultado exibido depois
 * do recálculo não bateria com o número digitado.
 */

/** `normalizeAlloc` (L170) — soma falsy devolve o objeto original, sem normalizar. */
export function normalizeAlloc(a: Record<string, number>): Record<string, number> {
  const vals = Object.entries(a).filter(([, v]) => Number(v) > 0);
  const soma = vals.reduce((t, [, v]) => t + Number(v), 0);
  if (!soma) return a;
  return Object.fromEntries(vals.map(([k, v]) => [k, Number(v) / soma]));
}

/**
 * Define a fração de um recurso e reparte o restante entre os demais.
 *
 * `pct` vem em porcentagem (0–100), como no input. Se os outros recursos estavam
 * todos em zero, o restante vai inteiro para o primeiro deles — é o que o original
 * faz, e é o que evita perder demanda quando se zera o recurso que tinha tudo.
 */
export function redistributeShare(
  current: Record<string, number>,
  resources: string[],
  target: string,
  pct: number,
): Record<string, number> {
  const v = Math.max(0, Math.min(1, (Number(pct) || 0) / 100));
  const a: Record<string, number> = { ...current, [target]: v };
  const others = resources.filter((x) => x !== target);
  const rem = 1 - v;
  const oldOther = others.reduce((s, x) => s + (a[x] || 0), 0);
  if (others.length) {
    if (oldOther > 0) {
      others.forEach((x) => {
        a[x] = rem * ((a[x] || 0) / oldOther);
      });
    } else {
      others.forEach((x, i) => {
        a[x] = i === 0 ? rem : 0;
      });
    }
  }
  return normalizeAlloc(a);
}
